import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useAuthContext } from '@retail/auth/data-access';
import { QueryOptions } from '@shared/fetch-utils';
import { buildUrl } from '@shared/utils';
import { useMPEFetch } from '@retail/app/hooks';
import { mpeApi } from '@retail/mgpris/config';
import {
  HierarchyFilterContextDTO,
  CentralAssortmentFilterContextDTO,
} from '@retail/my-assortment/types';

interface AssortmentFilterProps extends QueryOptions {
  assortmentId: number;
  supplierNumber?: number;
}

export function useFetchAssortmentHierarchyFilterOptions({
  assortmentId,
  supplierNumber,
  ...queryOptions
}: AssortmentFilterProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<HierarchyFilterContextDTO>({
    method: 'GET',
    queryKey: ['assortment-filter-options', assortmentId, supplierNumber],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.assortmentPrices.assortment.filterContext.getFullLinkPath({ assortmentId }),
      searchParams: { supplierNumber },
    }),
    ...queryOptions,
  });
}

export function useFetchCentralAssortmentMemberViewFilterOptions({
  assortmentId,
  ...queryOptions
}: AssortmentFilterProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<CentralAssortmentFilterContextDTO>({
    method: 'GET',
    queryKey: ['central-assortment-member-wiew-filter-options'],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.assortments.assortment.centralAssortments.filters.getFullLinkPath({
        assortmentId,
      }),
    }),
    ...queryOptions,
  });
}

export function useFetchCentralAssortmentFilters({
  assortmentId,
  ...queryOptions
}: AssortmentFilterProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<CentralAssortmentFilterContextDTO>({
    method: 'GET',
    queryKey: ['central-assortment-filter-options'],
    token,
    url: buildUrl({
      base: mpe.url,
      route: mpeApi.brandAssortments.assortment.filters.getFullLinkPath({
        assortmentId,
      }),
    }),
    ...queryOptions,
  });
}

interface SupplierFilterProps extends QueryOptions {
  assortmentId: number;
  supplierNumber: number;
  inAssortment: boolean;
}

export function useFetchSupplierHierarchyFilterOptions({
  assortmentId,
  supplierNumber,
  inAssortment,
  ...queryOptions
}: SupplierFilterProps) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMPEFetch<HierarchyFilterContextDTO>({
    method: 'GET',
    queryKey: ['orgUnit-assortments', supplierNumber, inAssortment],
    token,
    url: buildUrl({
      base: mpe.url,
      route:
        mpeApi.assortmentPrices.assortment.supplier.supplierNumber.filterContext.getFullLinkPath({
          assortmentId,
          supplierNumber,
        }),
      searchParams: { inAssortment },
    }),
    ...queryOptions,
  });
}
